import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Loader2, BrainCircuit, Wrench } from "lucide-react";

interface MessageAction {
  id: string;
  connector: string | null;
  tool_name: string;
  tool_input: any;
  tool_output: string | null;
  created_at: string;
  message_id: string | null;
}

interface ConversationDebugDialogProps {
  conversationId?: string | null;
  /** When provided, only show actions for this specific message */
  messageId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Pretty-print an LLM messages array for display */
function LLMRequestView({ input }: { input: any }) {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const messages: { role: string; content: string }[] = input?.messages ?? [];
  const model: string = input?.model ?? "unknown";

  return (
    <div className="mt-2 space-y-2">
      <div className="flex flex-wrap gap-2 text-[11px]">
        <span className="rounded bg-violet-100 text-violet-800 px-2 py-0.5 font-mono font-medium">
          model: {model}
        </span>
        <span className="rounded bg-blue-100 text-blue-800 px-2 py-0.5 font-mono">
          {messages.length} message{messages.length !== 1 ? "s" : ""}
        </span>
        {input?.tools_count != null && (
          <span className="rounded bg-green-100 text-green-800 px-2 py-0.5 font-mono">
            {input.tools_count} tool{input.tools_count !== 1 ? "s" : ""}
          </span>
        )}
      </div>
      <div className="space-y-1">
        {messages.map((m, i) => (
          <div
            key={i}
            className="rounded border border-border bg-background cursor-pointer"
            onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
          >
            <div className="flex items-center gap-2 px-2 py-1">
              <span
                className={`rounded px-1.5 py-0 text-[10px] font-medium ${
                  m.role === "user"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-orange-100 text-orange-700"
                }`}
              >
                {m.role}
              </span>
              <span className="truncate text-[11px] text-muted-foreground flex-1">
                {String(m.content ?? "").slice(0, 120)}
                {String(m.content ?? "").length > 120 ? "…" : ""}
              </span>
            </div>
            {expandedIdx === i && (
              <pre className="border-t px-2 py-1.5 whitespace-pre-wrap break-all text-[11px] max-h-48 overflow-auto bg-muted/50">
                {typeof m.content === "string"
                  ? m.content
                  : JSON.stringify(m.content, null, 2)}
              </pre>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const ConversationDebugDialog = ({
  conversationId,
  messageId,
  open,
  onOpenChange,
}: ConversationDebugDialogProps) => {
  const [actions, setActions] = useState<MessageAction[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (!conversationId && !messageId) return;
    setLoading(true);
    let query = supabase
      .from("message_actions")
      .select("id, connector, tool_name, tool_input, tool_output, created_at, message_id")
      .order("created_at", { ascending: true });

    if (messageId) {
      query = query.eq("message_id", messageId);
    } else if (conversationId) {
      query = query.eq("conversation_id", conversationId);
    }

    query.then(({ data }) => {
      setActions((data as MessageAction[]) || []);
      setLoading(false);
    });
  }, [open, conversationId, messageId]);

  const isLlmRequest = (a: MessageAction) => a.tool_name === "__llm_request__";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-sm">
            {messageId ? "LLM Debug – This Message" : "Conversation Debug"}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : actions.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No debug data found.</p>
        ) : (
          <ScrollArea className="flex-1 -mx-6 px-6">
            <div className="space-y-2 pb-2">
              {actions.map((a) => (
                <div
                  key={a.id}
                  className={`rounded-lg border p-3 text-xs cursor-pointer ${
                    isLlmRequest(a)
                      ? "border-violet-200 bg-violet-50/50"
                      : "border-border bg-muted/30"
                  }`}
                  onClick={() => setExpandedId(expandedId === a.id ? null : a.id)}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    {isLlmRequest(a) ? (
                      <Badge
                        variant="secondary"
                        className="text-[10px] px-1.5 py-0 bg-violet-100 text-violet-800 border-violet-200 gap-1"
                      >
                        <BrainCircuit className="h-3 w-3" />
                        LLM Input
                      </Badge>
                    ) : (
                      <>
                        {a.connector && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            {a.connector}
                          </Badge>
                        )}
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1.5 py-0 gap-1"
                        >
                          <Wrench className="h-3 w-3" />
                          Tool
                        </Badge>
                        <span className="font-mono font-medium text-foreground">
                          {a.tool_name}
                        </span>
                      </>
                    )}
                    <span className="ml-auto text-muted-foreground/60 text-[10px] shrink-0">
                      {new Date(a.created_at).toLocaleString(undefined, {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })}
                    </span>
                  </div>

                  {expandedId === a.id && (
                    isLlmRequest(a) ? (
                      <LLMRequestView input={a.tool_input} />
                    ) : (
                      <div className="mt-2 space-y-2">
                        <div>
                          <span className="text-muted-foreground font-medium">Input:</span>
                          <pre className="mt-1 whitespace-pre-wrap break-all rounded bg-muted p-2 text-[11px] max-h-40 overflow-auto">
                            {JSON.stringify(a.tool_input, null, 2)}
                          </pre>
                        </div>
                        <div>
                          <span className="text-muted-foreground font-medium">Output:</span>
                          <pre className="mt-1 whitespace-pre-wrap break-all rounded bg-muted p-2 text-[11px] max-h-40 overflow-auto">
                            {a.tool_output || "—"}
                          </pre>
                        </div>
                      </div>
                    )
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConversationDebugDialog;
